import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import { markVerified } from '../../utils/subscription';

export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) return;
    
    (async () => {
      try {
        const res = await apiService.post(`/api/email/verify/${token}`);
        const data = res.data;
        if (data?.success) {
          setStatus('success');
          setMessage(data.message || t('verify.success'));
          try { markVerified(); } catch {}
        } else {
          setStatus('error');
          setMessage(data?.message || t('verify.error'));
        }
      } catch (e) {
        setStatus('error');
        setMessage(t('verify.error'));
      }
    })();
  }, [token, t]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t('verify.verifying')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          {status === 'success' ? (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {t('verify.successTitle')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {message}
              </p>
              <Link 
                to="/"
                className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
              >
                {t('verify.backToHome')}
              </Link>
            </>
          ) : (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {t('verify.errorTitle')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {message}
              </p>
              <Link 
                to="/"
                className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
              >
                {t('verify.tryAgain')}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
